import { Controller } from "@hotwired/stimulus"
import { post, destroy } from "@rails/request.js"
import Hls from "hls.js"
import mpegts from "mpegts.js"

const PLAYLIST_ATTEMPTS = 12
const PLAYLIST_RETRY_MS = 2500

// Live camera streaming + recorded-file (VOD) playback for a JIMI device.
// The backend returns the stream URL right after accepting the turn-on
// command, without waiting for the device to actually confirm execution
// (see design.md decision #6) -- native <video> HLS support is used on
// Safari, hls.js everywhere else.
//
// A device can expose several camera channels (device.video_channels), each
// rendered as its own player and started/stopped independently: the device
// accepts simultaneous streams on different channels.
export default class extends Controller {
  static values = { imei: String, protocol: String }
  static targets = ["video", "liveStatus", "channel"]

  connect() {
    this.players = new Map()
    this.abandoned = new Set()
  }

  disconnect() {
    this.channels().forEach((channel) => {
      this.abandoned.add(String(channel))
      this.#destroyHls(channel)
    })
  }

  channels() {
    return this.channelTargets.map((element) => element.dataset.jimiStreamChannelParam)
  }

  async startLive(event) {
    await this.#start(this.#channelFrom(event))
  }

  async stopLive(event) {
    await this.#stop(this.#channelFrom(event))
  }

  // In parallel: each start waits for its own playlist to come up, and the
  // device serves channels independently -- queueing them would add every
  // channel's wait to the next one's.
  async startAll() {
    await Promise.all(this.channels().map((channel) => this.#start(channel)))
  }

  async stopAll() {
    await Promise.all(this.channels().map((channel) => this.#stop(channel)))
  }

  async playRecording(event) {
    const { file, from, to } = event.params
    const channel = String(event.params.channel || this.channels()[0])

    const response = await post(`/devices/${this.imeiValue}/vod_playback`, {
      body: JSON.stringify({ protocol: this.protocolValue, channel, file, from, to }),
      contentType: "application/json",
      responseKind: "json"
    })

    if (!response.ok) return

    const { url } = await response.json
    await this.#play(channel, url)
  }

  async #start(channel) {
    const status = this.#statusFor(channel)
    status.textContent = status.dataset.loadingText || "Iniciando..."

    const response = await post(`/devices/${this.imeiValue}/live_stream`, {
      body: JSON.stringify({ protocol: this.protocolValue, channel }),
      contentType: "application/json",
      responseKind: "json"
    })

    if (!response.ok) {
      status.textContent = "Não foi possível iniciar a transmissão."
      return
    }

    this.abandoned.delete(String(channel))

    const { url } = await response.json
    await this.#play(channel, url)
  }

  async #stop(channel) {
    this.abandoned.add(String(channel))

    await destroy(`/devices/${this.imeiValue}/live_stream`, {
      body: JSON.stringify({ protocol: this.protocolValue, channel }),
      contentType: "application/json"
    })

    this.#destroyHls(channel)
    this.#videoFor(channel).removeAttribute("src")
    this.#statusFor(channel).textContent = "Transmissão parada."
  }

  #channelFrom(event) {
    return String(event.params.channel)
  }

  #indexOf(channel) {
    return this.channels().indexOf(String(channel))
  }

  #videoFor(channel) {
    return this.videoTargets[this.#indexOf(channel)]
  }

  #statusFor(channel) {
    return this.liveStatusTargets[this.#indexOf(channel)]
  }

  // Live is HLS; recorded playback comes back as HTTP-FLV, which no browser
  // plays natively -- mpegts.js feeds it through Media Source Extensions.
  async #play(channel, url) {
    this.#destroyHls(channel)

    const video = this.#videoFor(channel)
    if (!video) return

    if (url.endsWith(".flv")) {
      this.#playFlv(channel, video, url)
      return
    }

    if (!(await this.#waitForPlaylist(channel, url))) return

    // hls.js first, native HLS only as fallback: Chrome answers "maybe" to
    // canPlayType("application/vnd.apple.mpegurl") without being able to
    // play HLS, so trusting it leaves a <video> with a src that never
    // starts. Native playback is really only Safari/iOS.
    if (Hls.isSupported()) {
      const hls = new Hls()
      this.players.set(String(channel), hls)
      hls.loadSource(url)
      hls.attachMedia(video)
      hls.on(Hls.Events.MANIFEST_PARSED, () => this.#startPlayback(channel, video))
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = url
      this.#startPlayback(channel, video)
    } else {
      this.#statusFor(channel).textContent = "Este navegador não reproduz o formato da transmissão."
      return
    }

    this.#statusFor(channel).textContent = "Ao vivo"
  }

  // play() rejects when the browser's autoplay policy blocks it. The promise
  // was being dropped, so the player just sat there with the controls
  // available and no hint of why.
  #startPlayback(channel, video) {
    video.play().catch(() => {
      this.#statusFor(channel).textContent = "Pronto. Use o play do player para iniciar."
    })
  }

  // The backend answers with the stream URL as soon as the hub accepts the
  // turn-on command, without waiting for the device to actually push (see
  // design.md decision #6) -- that takes a few seconds, and both hls.js and
  // native HLS give up on the first 404. A HEAD is no use here: the media
  // server answers 200 to any path, so the playlist itself has to be read.
  async #waitForPlaylist(channel, url) {
    const status = this.#statusFor(channel)

    for (let attempt = 1; attempt <= PLAYLIST_ATTEMPTS; attempt++) {
      if (this.abandoned.has(String(channel))) return false

      if (await this.#playlistReady(url)) return true

      status.textContent = `Aguardando o dispositivo… (${attempt}/${PLAYLIST_ATTEMPTS})`
      await new Promise((resolve) => setTimeout(resolve, PLAYLIST_RETRY_MS))
    }

    status.textContent = "O dispositivo não subiu a transmissão a tempo."
    return false
  }

  async #playlistReady(url) {
    try {
      const response = await fetch(url, { cache: "no-store" })
      if (!response.ok) return false

      return (await response.text()).trimStart().startsWith("#EXTM3U")
    } catch {
      // Stream not up yet: the media server refuses or resets the connection
      // until the device pushes. Expected during the wait, not an error.
      return false
    }
  }

  #playFlv(channel, video, url) {
    if (!mpegts.isSupported()) {
      this.#statusFor(channel).textContent = "Este navegador não reproduz o formato da gravação."
      return
    }

    const player = mpegts.createPlayer({ type: "flv", isLive: false, url })
    this.players.set(String(channel), player)
    player.attachMediaElement(video)
    player.load()
    this.#startPlayback(channel, video)
  }

  #destroyHls(channel) {
    const player = this.players.get(String(channel))
    if (!player) return

    player.destroy()
    this.players.delete(String(channel))
  }
}
